import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiSearch, FiList, FiPlay, FiCheckCircle } = FiIcons;

const RecruiterWorkflow = () => {
  const workflowSteps = [
    {
      icon: FiPlus,
      title: 'Create job & set screening rules',
      description: 'Define role requirements, skills, and automated screening criteria'
    },
    {
      icon: FiSearch,
      title: 'Source/invite candidates',
      description: 'Multi-channel posting and direct candidate invitations'
    },
    {
      icon: FiList,
      title: 'Review pre-scored list; shortlist',
      description: 'AI-powered candidate ranking with manual override capabilities'
    },
    {
      icon: FiPlay,
      title: 'Launch AI interviews',
      description: 'Automated interview invitations with customizable question sets'
    },
    {
      icon: FiCheckCircle,
      title: 'Compare panel scores, finalize decision',
      description: 'Comprehensive scoring comparison and final hiring decisions'
    }
  ];

  return (
    <section id="recruiter-workflow" className="py-16 lg:py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="font-display font-bold text-3xl lg:text-5xl text-slate-900 mb-6">
            Recruiter Workflow Made Simple
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Step-by-step guidance through the complete recruitment process
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left Column - Steps */}
          <motion.div
            className="space-y-8"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            {workflowSteps.map((step, index) => (
              <motion.div
                key={index}
                className="flex items-start space-x-4"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <SafeIcon icon={step.icon} className="w-6 h-6 text-indigo-600" />
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-lg text-slate-900 mb-2">
                    {step.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </motion.div>
            ))}

            <motion.div
              className="bg-indigo-50 border border-indigo-200 rounded-lg p-6 mt-8"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              viewport={{ once: true }}
            >
              <p className="text-indigo-700 font-medium">
                Each step logs activity + candidate communication for compliance.
              </p>
            </motion.div>
          </motion.div>

          {/* Right Column - Screenshot Mockup */}
          <motion.div
            className="relative"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
              {/* Browser Header */}
              <div className="bg-slate-100 px-4 py-3 flex items-center space-x-2">
                <div className="flex space-x-2">
                  <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                  <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                  <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                </div>
                <div className="flex-1 bg-white rounded px-3 py-1 text-sm text-slate-500">
                  instashortlist.com/dashboard
                </div>
              </div>

              {/* Dashboard Content */}
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-semibold text-slate-900">Active Jobs</h3>
                  <button className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm">
                    + New Job
                  </button>
                </div>

                <div className="space-y-4">
                  {[
                    { title: 'Senior React Developer', candidates: 23, status: 'Shortlisting' },
                    { title: 'Product Manager', candidates: 45, status: 'Interviewing' },
                    { title: 'UX Designer', candidates: 12, status: 'Reviewing' }
                  ].map((job, index) => (
                    <div key={index} className="bg-slate-50 rounded-lg p-4 flex items-center justify-between">
                      <div>
                        <div className="font-medium text-slate-900">{job.title}</div>
                        <div className="text-sm text-slate-500">{job.candidates} candidates</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-medium text-indigo-600">{job.status}</div>
                        <div className="text-xs text-slate-500">Updated 2h ago</div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 grid grid-cols-3 gap-4">
                  <div className="bg-blue-50 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-blue-600">156</div>
                    <div className="text-xs text-blue-600">Total Applications</div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-green-600">89</div>
                    <div className="text-xs text-green-600">Shortlisted</div>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-purple-600">34</div>
                    <div className="text-xs text-purple-600">Interviewed</div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default RecruiterWorkflow;